// src/components/motion/AnswerFeedback.tsx
// クイズ解答時の正誤フィードバックのモーション層。
//  - AnswerFeedback: 正誤バナー（アイコン＋メッセージのスケール/フェードポップ）。
//    正解時に celebrateSmall() を「1問につき1回」だけ発火する。
//  - AnswerOption: 各選択肢のラッパー。正解肢は軽いスケールポップ＋緑チェック、
//    誤選択肢は横シェイク＋赤チェックで示す。
// いずれも useReducedMotion を尊重し、reduced-motion 時はアニメーションを省いて最終状態のみ表示する。
import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { motion, useReducedMotion } from 'framer-motion';
import { celebrateSmall } from '@/components/motion';
import { motion as motionTokens, radius } from '@/theme/tokens';

interface AnswerFeedbackProps {
    isCorrect: boolean;
    message: React.ReactNode;
    /** この値が変わるたびに正解セレブレーションを1回だけ許可する（＝1問1回）。 */
    celebrateKey?: string | number;
}

/** 正誤バナー。アイコン＋メッセージを穏やかにポップ表示し、正解時に金の紙吹雪を1度だけ出す。 */
export const AnswerFeedback: React.FC<AnswerFeedbackProps> = ({ isCorrect, message, celebrateKey }) => {
    const reduce = useReducedMotion();
    const firedForRef = useRef<string | number | undefined>(undefined);

    useEffect(() => {
        // 正解、かつこの celebrateKey でまだ発火していない場合のみ1回発火。
        // 同一マウント内の再レンダーでは deps 不変のため再実行されない（＝毎レンダー発火しない）。
        if (isCorrect && firedForRef.current !== celebrateKey) {
            firedForRef.current = celebrateKey;
            celebrateSmall(); // 内部で prefers-reduced-motion を尊重
        }
    }, [isCorrect, celebrateKey]);

    return (
        <motion.div
            initial={reduce ? false : { scale: 0.94, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: motionTokens.duration.base, ease: motionTokens.ease }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isCorrect ? (
                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                ) : (
                    <CancelIcon sx={{ color: 'error.main' }} />
                )}
                <Typography variant="h6" color={isCorrect ? 'success.main' : 'error.main'}>
                    {message}
                </Typography>
            </Box>
        </motion.div>
    );
};

interface AnswerOptionProps {
    /** 解答が公開済みか（＝採点結果を表示する状態か）。 */
    revealed: boolean;
    /** この選択肢が正解肢か。 */
    correct: boolean;
    /** この選択肢がユーザーの誤選択か。 */
    selectedWrong: boolean;
    children: React.ReactNode;
}

/** 選択肢1件のラッパー。公開時に正解肢/誤選択肢を色とモーションで示す。 */
export const AnswerOption: React.FC<AnswerOptionProps> = ({ revealed, correct, selectedWrong, children }) => {
    const reduce = useReducedMotion();
    const showCorrect = revealed && correct;
    const showWrong = revealed && selectedWrong;

    const animate = reduce
        ? undefined
        : showWrong
            ? { x: [0, -6, 6, -4, 4, 0] } // 横シェイク
            : showCorrect
                ? { scale: [0.98, 1.02, 1] } // 穏やかなポップ
                : { x: 0, scale: 1 };

    return (
        <motion.div
            animate={animate}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            style={{ borderRadius: radius.sm }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: `${radius.sm}px`,
                    px: 1,
                    my: 0.25,
                    transition: 'background-color 160ms ease, border-color 160ms ease',
                    bgcolor: showCorrect
                        ? 'rgba(76,175,125,0.12)'
                        : showWrong
                            ? 'rgba(229,72,77,0.10)'
                            : 'transparent',
                    border: showCorrect
                        ? '1px solid rgba(76,175,125,0.5)'
                        : showWrong
                            ? '1px solid rgba(229,72,77,0.4)'
                            : '1px solid transparent',
                }}
            >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>{children}</Box>
                {showCorrect || showWrong ? (
                    <motion.span
                        initial={reduce ? false : { scale: 0.6, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: motionTokens.duration.fast, ease: motionTokens.ease }}
                        style={{ display: 'inline-flex', marginLeft: 8, flexShrink: 0 }}
                    >
                        {showCorrect ? (
                            <CheckCircleIcon sx={{ color: 'success.main' }} fontSize="small" />
                        ) : (
                            <CancelIcon sx={{ color: 'error.main' }} fontSize="small" />
                        )}
                    </motion.span>
                ) : null}
            </Box>
        </motion.div>
    );
};

export default AnswerFeedback;
